import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import Select from 'react-select';
import { API_BASE_URL } from '../config/apiConfig';

const PRICE_RANGES = [
  { value: 'under-150', label: 'Under $150', min: 0, max: 149.99 },
  { value: '150-175', label: '$150 - $175', min: 150, max: 175 },
  { value: '175-200', label: '$175 - $200', min: 175, max: 200 },
  { value: 'over-200', label: 'Over $200', min: 200.01, max: Number.POSITIVE_INFINITY },
];

const DEFAULT_FILTERS = {
  style: [],
  color: [],
  price: [],
  season: [],
  sleeveLength: [],
  pattern: [],
  fabricWeight: [],
  threadCount: [],
  fabricWeave: [],
  composition: [],
  sale: false,
};

const normalize = (value) => (value || '').toString().trim().toLowerCase();

const splitMultiValue = (value) => {
  if (!value) return [];
  return value
    .toString()
    .split(/[\/,|]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const CategoryPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [category, setCategory] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categoryHierarchy, setCategoryHierarchy] = useState([]);
  const [expandedParentId, setExpandedParentId] = useState(null);
  const [subSubCategoryFilter, setSubSubCategoryFilter] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(DEFAULT_FILTERS);
  const [activeProductDetailSection, setActiveProductDetailSection] = useState('style');

  const generateSlug = (title) => title.toLowerCase().replace(/\s+/g, '-');

  useEffect(() => {
    fetch(`${API_BASE_URL}/category/api/categories-with-products`)
      .then((res) => res.json())
      .then((data) => {
        const matchedCategory = data.find((cat) => cat.slug === slug);
        if (matchedCategory) {
          setCategory(matchedCategory);
          setAllProducts(matchedCategory.products || []);
          setFilteredProducts(matchedCategory.products || []);
        }
      });

    fetch(`${API_BASE_URL}/category/api/category-hierarchy`)
      .then((res) => res.json())
      .then((data) => {
        setCategoryHierarchy(data);

        for (const parent of data) {
          for (const sub of parent.subCategories) {
            const subSlug = generateSlug(sub.categoryTitle);
            if (slug === subSlug) {
              setExpandedParentId(parent.categoryID);
              return;
            }
            for (const subsub of sub.subSubCategories || []) {
              const subSubSlug = generateSlug(subsub.categoryTitle);
              if (slug === subSubSlug) {
                setExpandedParentId(parent.categoryID);
                return;
              }
            }
          }
        }
      });
  }, [slug]);

  useEffect(() => {
    setSelectedFilters(DEFAULT_FILTERS);
    setSubSubCategoryFilter(null);
    setActiveProductDetailSection('style');
  }, [slug]);

  const getFabricBasePrice = (fabric) => {
    const price = fabric?.basePrice;
    if (typeof price === 'number') return price;
    const parsed = Number(price);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getFabricStyleOptions = (fabric) => fabric?.styleOptions || [];

  const getStyleValues = (fabric) => {
    const values = splitMultiValue(fabric?.suggestedStyle);
    return values.length ? values : [];
  };

  const getColorValues = (fabric) => splitMultiValue(fabric?.fabricColour);

  const getPatternValues = (fabric) => {
    return getFabricStyleOptions(fabric)
      .filter((styleOption) => normalize(styleOption?.styleType).includes('pattern'))
      .map((styleOption) => styleOption?.styleName)
      .filter(Boolean);
  };

  const getSleeveLengthValues = (fabric) => {
    const values = [];
    getFabricStyleOptions(fabric).forEach((styleOption) => {
      const styleType = normalize(styleOption?.styleType);
      if (styleType.includes('sleeve length')) {
        values.push(styleOption?.styleName);
      }
      const sleeveGroup = styleOption?.sleeveGroup;
      if (sleeveGroup) {
        values.push(sleeveGroup);
      }
    });

    return values.filter(Boolean);
  };

  const allFabrics = useMemo(
    () => allProducts.flatMap((product) => product.fabrics || []),
    [allProducts]
  );

  const filterOptions = useMemo(() => {
    const uniq = (items) => [...new Set(items.filter(Boolean))];

    const style = uniq(allFabrics.flatMap((fabric) => getStyleValues(fabric))).sort();
    const color = uniq(allFabrics.flatMap((fabric) => getColorValues(fabric))).sort();
    const seasonOrder = ['All-Season', 'Summer', 'Winter'];
    const season = seasonOrder;

    const sleeveLength = uniq(allFabrics.flatMap((fabric) => getSleeveLengthValues(fabric))).sort();
    const pattern = uniq(allFabrics.flatMap((fabric) => getPatternValues(fabric))).sort();
    const fabricWeight = uniq(allFabrics.map((fabric) => fabric.weight?.toString())).sort((a, b) => Number(a) - Number(b));
    const threadCount = uniq(allFabrics.map((fabric) => fabric.threadCount));
    const fabricWeave = uniq(allFabrics.map((fabric) => fabric.weave)).sort();
    const composition = uniq(allFabrics.map((fabric) => fabric.composition)).sort();

    return {
      style,
      color,
      price: PRICE_RANGES,
      season,
      sleeveLength,
      pattern,
      fabricWeight,
      threadCount,
      fabricWeave,
      composition,
    };
  }, [allFabrics]);

  useEffect(() => {
    const sectionOrder = [
      { key: 'style', hasContent: filterOptions.style?.length > 0 },
      { key: 'color', hasContent: filterOptions.color?.length > 0 },
      { key: 'price', hasContent: filterOptions.price?.length > 0 },
      { key: 'sale', hasContent: true },
      { key: 'season', hasContent: filterOptions.season?.length > 0 },
      { key: 'sleeveLength', hasContent: filterOptions.sleeveLength?.length > 0 },
      { key: 'pattern', hasContent: filterOptions.pattern?.length > 0 },
      { key: 'fabricWeight', hasContent: filterOptions.fabricWeight?.length > 0 },
      { key: 'threadCount', hasContent: filterOptions.threadCount?.length > 0 },
      { key: 'fabricWeave', hasContent: filterOptions.fabricWeave?.length > 0 },
      { key: 'composition', hasContent: filterOptions.composition?.length > 0 },
    ];

    const firstAvailable = sectionOrder.find((section) => section.hasContent)?.key || 'style';
    const activeStillAvailable = sectionOrder.some(
      (section) => section.key === activeProductDetailSection && section.hasContent
    );

    if (activeProductDetailSection && !activeStillAvailable) {
      setActiveProductDetailSection(firstAvailable);
    }
  }, [filterOptions, activeProductDetailSection]);

  const sortOptions = [
    { label: 'Recommended', value: null },
    { label: 'Price: Low to High', value: 'low-to-high' },
    { label: 'Price: High to Low', value: 'high-to-low' },
  ];

  const isPriceMatch = (basePrice, selectedRanges) => {
    if (!selectedRanges.length) return true;

    return selectedRanges.some((rangeValue) => {
      const range = PRICE_RANGES.find((item) => item.value === rangeValue);
      if (!range) return false;
      return basePrice >= range.min && basePrice <= range.max;
    });
  };

  const isValueMatch = (fabricValues, selectedValues) => {
    if (!selectedValues.length) return true;
    const normalizedFabric = fabricValues.map(normalize);
    return selectedValues.some((selected) => normalizedFabric.includes(normalize(selected)));
  };

  const isFabricMatch = (fabric) => {
    const basePrice = getFabricBasePrice(fabric);

    return (
      isValueMatch(getStyleValues(fabric), selectedFilters.style) &&
      isValueMatch(getColorValues(fabric), selectedFilters.color) &&
      isPriceMatch(basePrice, selectedFilters.price) &&
      isValueMatch([fabric.season], selectedFilters.season) &&
      isValueMatch(getSleeveLengthValues(fabric), selectedFilters.sleeveLength) &&
      isValueMatch(getPatternValues(fabric), selectedFilters.pattern) &&
      isValueMatch([fabric.weight?.toString()], selectedFilters.fabricWeight) &&
      isValueMatch([fabric.threadCount], selectedFilters.threadCount) &&
      isValueMatch([fabric.weave], selectedFilters.fabricWeave) &&
      isValueMatch([fabric.composition], selectedFilters.composition)
    );
  };

  useEffect(() => {
    let result = [...allProducts];

    if (subSubCategoryFilter?.value) {
      result = result.filter(
        (product) =>
          product.subSubCategory?.categoryTitle?.toLowerCase().trim() ===
          subSubCategoryFilter.value.toLowerCase().trim()
      );
    }

    result = result
      .map((product) => {
        const fabrics = product.fabrics || [];
        const matchingFabrics = fabrics.filter((fabric) => isFabricMatch(fabric));

        if (!matchingFabrics.length) {
          return null;
        }

        if (
          selectedFilters.sale &&
          !(Number(product.discountPrice) > 0 || Number(product.discountPercentage) > 0)
        ) {
          return null;
        }

        return {
          ...product,
          fabrics: matchingFabrics,
        };
      })
      .filter(Boolean);

    if (sortOrder?.value) {
      result.sort((a, b) => {
        const priceA = getFabricBasePrice(a.fabrics?.[0]);
        const priceB = getFabricBasePrice(b.fabrics?.[0]);
        return sortOrder.value === 'low-to-high' ? priceA - priceB : priceB - priceA;
      });
    }

    setFilteredProducts(result);
  }, [selectedFilters, subSubCategoryFilter, sortOrder, allProducts]);

  const filterKey = `${JSON.stringify(selectedFilters)}-${subSubCategoryFilter?.value || 'all'}-${sortOrder?.value || 'none'}`;

  const handleSubCategoryClick = (title) => {
    const subSlug = generateSlug(title);
    setSubSubCategoryFilter(null);
    navigate(`/category/${subSlug}`);
    setShowMobileFilters(false);
  };

  const handleSubSubCategoryClick = (subSubTitle, subTitle) => {
    const subSlug = generateSlug(subTitle);
    if (slug === subSlug) {
      setSubSubCategoryFilter({ value: subSubTitle, label: subSubTitle });
    } else {
      navigate(`/category/${subSlug}`, { state: { filterSubSub: subSubTitle } });
    }
    setShowMobileFilters(false);
  };

  useEffect(() => {
    const state = window.history.state?.usr?.filterSubSub;
    if (state) {
      setSubSubCategoryFilter({ value: state, label: state });
      window.history.replaceState({}, document.title);
    }
  }, []);

  const toggleMultiFilter = (group, value) => {
    setSelectedFilters((prev) => {
      const current = prev[group] || [];
      const exists = current.includes(value);
      const next = exists ? current.filter((item) => item !== value) : [...current, value];
      return { ...prev, [group]: next };
    });
  };

  const renderFilterCheckboxOptions = (groupKey, options) => {
    if (!options || options.length === 0) return null;

    return (
      <>
        {options.map((option) => {
          const value = typeof option === 'string' ? option : option.value;
          const label = typeof option === 'string' ? option : option.label;
          return (
            <div className="form-check mb-1 category-filter-option" key={`${groupKey}-${value}`}>
              <input
                className="form-check-input category-filter-checkbox"
                type="checkbox"
                id={`${groupKey}-${value}`}
                checked={(selectedFilters[groupKey] || []).includes(value)}
                onChange={() => toggleMultiFilter(groupKey, value)}
              />
              <label className="form-check-label" htmlFor={`${groupKey}-${value}`}>
                {label}
              </label>
            </div>
          );
        })}
      </>
    );
  };

  const renderFilterAccordionSection = (title, sectionKey, content) => {
    if (!content) return null;
    const isOpen = activeProductDetailSection === sectionKey;

    return (
      <div className="mb-2" key={sectionKey}>
        <button
          type="button"
          className="w-100 d-flex justify-content-between align-items-center border-0 bg-transparent px-0 py-1"
          onClick={() =>
            setActiveProductDetailSection((prev) => (prev === sectionKey ? null : sectionKey))
          }
          aria-expanded={isOpen}
          aria-controls={`product-filter-${sectionKey}`}
        >
          <span className="fw-bold">{title}</span>
          <span aria-hidden="true" style={{ fontSize: '12px' }}>{isOpen ? '▴' : '▾'}</span>
        </button>
        {isOpen && (
          <div id={`product-filter-${sectionKey}`} className="pb-2">
            {content}
          </div>
        )}
      </div>
    );
  };

  const renderFilteredDropdowns = () => {
    return (
      <>
        <h6 className="mb-3 fw-bold">Categories</h6>
        {categoryHierarchy.map((parent) => {
          const isExpanded = parent.categoryID === expandedParentId;
          return (
            <div key={parent.categoryID} className="mb-2">
              <div
                className="fw-bold mb-1"
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  setExpandedParentId(expandedParentId === parent.categoryID ? null : parent.categoryID)
                }
              >
                {parent.categoryTitle}
              </div>
              {isExpanded && (
                <ul className="list-unstyled ms-3">
                  {parent.subCategories.map((sub) => {
                    const subSlug = generateSlug(sub.categoryTitle);
                    const isActiveSub =
                      slug === subSlug ||
                      sub.subSubCategories.some((ss) => generateSlug(ss.categoryTitle) === slug);
                    return (
                      <li key={sub.categoryID}>
                        <div
                          onClick={() => handleSubCategoryClick(sub.categoryTitle)}
                          style={{ cursor: 'pointer', fontWeight: 500 }}
                        >
                          {sub.categoryTitle}
                        </div>
                        {isActiveSub && sub.subSubCategories.length > 0 && (
                          <ul className="list-unstyled ms-3">
                            {sub.subSubCategories.map((subsub) => (
                              <li
                                key={subsub.categoryID}
                                onClick={() =>
                                  handleSubSubCategoryClick(subsub.categoryTitle, sub.categoryTitle)
                                }
                                style={{ cursor: 'pointer', color: '#555' }}
                              >
                                {subsub.categoryTitle}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}

        <hr className="my-3" />
        <h6 className="mb-3 fw-bold">Product Details</h6>

        {[
          {
            title: 'Style',
            key: 'style',
            content: renderFilterCheckboxOptions('style', filterOptions.style),
          },
          {
            title: 'Color',
            key: 'color',
            content: renderFilterCheckboxOptions('color', filterOptions.color),
          },
          {
            title: 'Price',
            key: 'price',
            content: renderFilterCheckboxOptions('price', filterOptions.price),
          },
          {
            title: 'Sale',
            key: 'sale',
            content: (
              <div className="form-check mb-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="sale-filter"
                  checked={selectedFilters.sale}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      sale: e.target.checked,
                    }))
                  }
                />
                <label className="form-check-label" htmlFor="sale-filter">
                  Sale
                </label>
              </div>
            ),
          },
          {
            title: 'Season',
            key: 'season',
            content: renderFilterCheckboxOptions('season', filterOptions.season),
          },
          {
            title: 'Sleeve Length',
            key: 'sleeveLength',
            content: renderFilterCheckboxOptions('sleeveLength', filterOptions.sleeveLength),
          },
          {
            title: 'Pattern',
            key: 'pattern',
            content: renderFilterCheckboxOptions('pattern', filterOptions.pattern),
          },
          {
            title: 'Fabric Weight',
            key: 'fabricWeight',
            content: renderFilterCheckboxOptions('fabricWeight', filterOptions.fabricWeight),
          },
          {
            title: 'Thread Count',
            key: 'threadCount',
            content: renderFilterCheckboxOptions('threadCount', filterOptions.threadCount),
          },
          {
            title: 'Fabric Weave',
            key: 'fabricWeave',
            content: renderFilterCheckboxOptions('fabricWeave', filterOptions.fabricWeave),
          },
          {
            title: 'Composition',
            key: 'composition',
            content: renderFilterCheckboxOptions('composition', filterOptions.composition),
          },
        ]
          .filter((section) => Boolean(section.content))
          .map((section) =>
            renderFilterAccordionSection(section.title, section.key, section.content)
          )}
      </>
    );
  };

  const renderSidebar = () => (
    <div
      className="d-none d-md-block col-md-3"
      style={{
        position: 'sticky',
        top: '100px',
        alignSelf: 'flex-start',
        height: 'fit-content',
      }}
    >
      {renderFilteredDropdowns()}
    </div>
  );

  return (
    <>
      <style>{`
        .category-filter-option .category-filter-checkbox {
          opacity: 1 !important;
          position: relative !important;
          -webkit-appearance: checkbox !important;
          appearance: checkbox !important;
          width: 16px;
          height: 16px;
          margin-top: 0.22rem;
          margin-right: 0.35rem;
          border: 1.5px solid #b8bfd0 !important;
          background-color: #fff !important;
          accent-color: #1f4aa8;
        }

        .category-filter-option .form-check-label::before,
        .category-filter-option .form-check-label::after {
          display: none !important;
          content: none !important;
        }
      `}</style>
      <Header />
      <main className="rbt-main-wrapper">
        <section>
          <div className="container mt-5">
            {category ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>{category.categoryTitle}</h2>
                  <button
                    className="d-md-none btn btn-outline-dark"
                    onClick={() => setShowMobileFilters(true)}
                  >
                    Show Filters
                  </button>
                  <div style={{ width: '250px' }} className="d-none d-md-block">
                    <Select
                      options={sortOptions}
                      value={sortOrder}
                      onChange={setSortOrder}
                      placeholder="Sort Products"
                      isClearable
                      classNamePrefix="react-select"
                    />
                  </div>
                </div>

                <div className="row">
                  {renderSidebar()}

                  <div className="col-md-9">
                    <div className="row g-4">
                      {filteredProducts.length > 0 ? (
                        filteredProducts.map((product) => (
                          <ProductCard product={product} key={`${product.productID}-${filterKey}`} />
                        ))
                      ) : (
                        <p className="text-center">No products match the selected filters.</p>
                      )}
                    </div>
                  </div>
                </div>

                {showMobileFilters && (
                  <div
                    className="position-fixed top-0 start-0 w-100 h-100 bg-white p-4"
                    style={{ zIndex: 1050, overflowY: 'auto' }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Filters</h5>
                      <button className="btn-close" onClick={() => setShowMobileFilters(false)}></button>
                    </div>
                    <hr />
                    <div className="mb-3">
                      <label className="form-label fw-bold">Sort Products</label>
                      <Select
                        options={sortOptions}
                        value={sortOrder}
                        onChange={setSortOrder}
                        placeholder="Sort Products"
                        isClearable
                        classNamePrefix="react-select"
                      />
                    </div>
                    {renderFilteredDropdowns()}
                  </div>
                )}
              </>
            ) : (
              <p className="text-center">Loading or no matching category found.</p>
            )}
          </div>
        </section>
        <Footer />
      </main>
    </>
  );
};

export default CategoryPage;
